import type { ReactNode } from "react";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { StatusChip } from "../status-chip";
import type { Credential, CredentialVerification } from "../../types";
import { CredentialDetailBody } from "./credential-details/credential-detail-renderers";
import { DocumentRow } from "./document-row";
import { CredentialVerifications } from "./credential-verifications";
import type { CredentialDocument } from "../../types";
import { credentialDisplayLabel } from "@/features/credentials/lib/credential-display-label";
import { credentialRowSummary } from "./credential-details/credential-summaries";

const POPOVER_VIEWPORT_PADDING_PX = 16;

/**
 * Everything a credential holds, without opening the editor.
 *
 * The card highlights the identifying facts; this view contains the full
 * record and supporting documents without requiring a trip through Edit.
 *
 * The body is rendered per credential type
 * (`credential-detail-renderers.tsx`): a malpractice claims history is a list
 * of incidents and a driver's licence is a number and a date, so they cannot
 * share one layout.
 */
export function CredentialDetailsPopover({
  credential,
  children,
  onEdit,
  onOpenDocument,
  onOpenVerificationEvidence,
  isOpeningDocument,
  onRemoveDocument,
}: {
  credential: Credential;
  /** The clickable summary text. */
  children: ReactNode;
  /** Editing lives here rather than on the card: this is already where the
   * credential's values are, and a button per row made the list mostly
   * buttons. Absent for readers who may not edit. */
  onEdit?: (credential: Credential) => void;
  onOpenDocument?: (document: CredentialDocument) => void;
  onOpenVerificationEvidence?: (verification: CredentialVerification) => void;
  isOpeningDocument?: boolean;
  onRemoveDocument?: (document: CredentialDocument) => void;
}) {
  const verifications = credential.verifications ?? [];
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={POPOVER_VIEWPORT_PADDING_PX}
        // Tall records (personal info, a long claims history) scroll rather
        // than running off the screen.
        className="max-h-[70vh] w-96 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-2xl border-slate-200 bg-white p-5 shadow-lg"
      >
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <p className="min-w-0 text-sm font-semibold text-slate-900">
            {credentialRowSummary(credential)?.title ??
              credentialDisplayLabel({ credential })}
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            <StatusChip
              status={credential.status}
              requirement={credential.requirement}
              label={
                credential.status === "source-supplied"
                  ? credential.provenance?.label
                  : null
              }
            />
            {onEdit && (
              // A labelled button rather than a bare pencil: there is room in a
              // popover, and an icon alone made the reader guess.
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 rounded-xl px-2.5 text-xs"
                onClick={() => onEdit(credential)}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                Edit
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <CredentialDetailBody credential={credential} />

          {credential.provenance && (
            <div className="rounded-xl border border-primary/10 bg-primary/5 px-3 py-2 text-xs text-primary">
              <p className="font-medium">{credential.provenance.label}</p>
              {credential.provenance.description && (
                <p className="mt-0.5">{credential.provenance.description}</p>
              )}
              {credential.provenance.retrievedAt && (
                <p className="mt-0.5 tabular-nums">
                  Pulled{" "}
                  {new Date(
                    credential.provenance.retrievedAt,
                  ).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          {verifications.length > 0 && (
            <div className="border-t pt-3">
              <CredentialVerifications
                verifications={verifications}
                onOpenEvidence={onOpenVerificationEvidence}
              />
            </div>
          )}

          {/* Documents live here rather than on the card. A file name is rarely
              the thing a reader is looking for — the credential's values are —
              and a list of them pushed every other credential off the screen.
              The exception is a document-substance type like "Other", whose
              card headline already IS the file's title. */}
          {credential.documents.length > 0 && (
            <div className="border-t pt-2">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {credential.documents.length === 1
                  ? "Document"
                  : `Documents (${credential.documents.length})`}
              </p>
              <div className="divide-y divide-slate-100">
                {credential.documents.map((document) => (
                  <DocumentRow
                    key={document.id}
                    document={document}
                    onOpen={onOpenDocument}
                    isOpening={isOpeningDocument}
                    onRemove={onRemoveDocument}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
